"use client";

import { useEffect } from "react";
import Link from "next/link";

/**
 * Root error boundary. Must be a Client Component, and must render its own
 * markup — it replaces the page, not the layout. The real error is logged
 * server-side; the digest is the only safe handle to show a customer, and it is
 * what ties their report to a server log line.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("[app]", error);
  }, [error]);

  return (
    <main className="flex flex-1 flex-col items-center justify-center px-4 py-20 text-center">
      <h1 className="text-3xl font-bold tracking-tight text-ink">Something went wrong</h1>
      <p className="mt-3 max-w-sm text-pretty leading-relaxed text-body">
        That&apos;s on us. Try again — if it keeps happening, let us know.
      </p>
      {error.digest && (
        <p className="mt-2 text-xs text-muted">Reference {error.digest}</p>
      )}
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <button
          type="button"
          onClick={reset}
          className="inline-flex items-center justify-center rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-dark"
        >
          Try again
        </button>
        <Link
          href="/"
          className="inline-flex items-center justify-center rounded-full border border-line px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-brand-tint"
        >
          Go home
        </Link>
      </div>
    </main>
  );
}
